import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getRestaurantTemplate } from '@onepos/shared';
import { Alert, Badge, Button, Icon, Modal, SelectField, Spinner, Tabs, TextAreaField, useAction, useAsync } from '@onepos/ui';
import { APP_URL, api } from '../lib/api';
import { useAuth, useMe } from '../lib/auth';
import { STATUS, fmtDate, fmtMoneyShort, fmtPhone } from '../lib/format';
import type { TenantDetail, TenantStatus } from '../lib/types';
import { OverviewTab } from './restaurant/OverviewTab';
import { ModulesTab } from './restaurant/ModulesTab';
import { SettingsTab } from './restaurant/SettingsTab';
import { DevicesTab } from './restaurant/DevicesTab';
import { RolesTab } from './restaurant/RolesTab';
import { HistoryTab } from './restaurant/HistoryTab';
import { LoginTab } from './restaurant/LoginTab';
import { TariffTab } from './restaurant/TariffTab';
import { BillingTab } from './restaurant/BillingTab';
import { ImportFastModal } from './restaurant/ImportFastModal';

type TabKey = 'umumiy' | 'obuna' | 'tarif' | 'sozlamalar' | 'qurilmalar' | 'rollar' | 'jurnal';

export function RestaurantPage() {
  const { id = '' } = useParams();
  const [tab, setTab] = useState<TabKey>('umumiy');
  const [statusOpen, setStatusOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const me = useMe();
  const { data, error, loading, reload } = useAsync(() => api.rpc<TenantDetail>('platform_tenant', { p_tenant: id }), [id]);

  if (loading && !data) return <Spinner />;
  if (error && !data) {
    return (
      <>
        <BackLink />
        <Alert tone="danger" title="Restoranni ochib bo‘lmadi">
          {error.message}
        </Alert>
      </>
    );
  }
  if (!data) return null;

  const t = data.tenant;
  const activeDevices = data.terminals.filter((x) => x.is_active).length;
  const pos = data.limits?.pos;
  const canSupport = me.role !== 'billing';
  const canStatus = me.role !== 'support';

  return (
    <>
      <header className="cn-page-head">
        <div style={{ minWidth: 0 }}>
          <BackLink />
          <h1 className="cn-title-row">
            {t.name}
            <Badge tone={STATUS[t.status].tone}>{STATUS[t.status].label}</Badge>
          </h1>
          <p>
            {t.city ?? '—'} · {getRestaurantTemplate(t.template_key)?.label ?? t.template_key} · {data.plan.label} tarifi ·
            ochilgan {fmtDate(t.created_at)} · ega {fmtPhone(t.owner_phone)}
          </p>
        </div>
        <div className="cn-row">
          {canSupport && t.status !== 'deleted' && data.counts.products === 0 && data.counts.raw_materials === 0 && (
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              <Icon name="download" /> Fast’dan import
            </Button>
          )}
          {canSupport && t.status !== 'deleted' && (
            <Button variant="secondary" onClick={() => setSupportOpen(true)}>
              <Icon name="shield" /> Yordam rejimi
            </Button>
          )}
          {canStatus && (
            <Button variant="secondary" onClick={() => setStatusOpen(true)}>
              <Icon name="power" /> Holat
            </Button>
          )}
        </div>
      </header>

      {error && <Alert tone="danger">{error.message}</Alert>}

      <section className="cn-stats cn-stats--compact" aria-label="Qisqacha">
        <Stat label="Mahsulotlar" value={data.counts.products} />
        <Stat label="Xodimlar" value={`${data.counts.staff} / ${data.plan.max_staff}`} />
        <Stat label="Kassalar" value={pos ? `${pos.used} / ${pos.max}` : '—'} />
        <Stat label="Buyurtmalar (30 kun)" value={data.counts.orders_30d} />
        <Stat label="Savdo (30 kun)" value={fmtMoneyShort(data.counts.revenue_30d)} />
      </section>

      <Tabs<TabKey>
        label="Restoran bo‘limlari"
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'umumiy', label: 'Umumiy' },
          { key: 'obuna', label: 'Obuna va to‘lovlar' },
          { key: 'tarif', label: 'Tarif va bo‘limlar' },
          { key: 'sozlamalar', label: 'Sozlamalar' },
          { key: 'qurilmalar', label: 'Kirish va qurilmalar', count: activeDevices },
          { key: 'rollar', label: 'Rollar', count: data.roles.length },
          { key: 'jurnal', label: 'Jurnal' },
        ]}
      />

      {tab === 'umumiy' && <OverviewTab data={data} reload={reload} />}
      {tab === 'obuna' && <BillingTab data={data} reload={reload} />}
      {tab === 'tarif' && (
        <>
          <TariffTab data={data} reload={reload} />
          <ModulesTab data={data} reload={reload} />
        </>
      )}
      {tab === 'sozlamalar' && <SettingsTab data={data} reload={reload} />}
      {tab === 'qurilmalar' && (
        <>
          <LoginTab data={data} />
          <DevicesTab data={data} reload={reload} />
        </>
      )}
      {tab === 'rollar' && <RolesTab data={data} />}
      {tab === 'jurnal' && <HistoryTab data={data} />}

      <StatusModal key={`${t.status}-${String(statusOpen)}`} open={statusOpen} onClose={() => setStatusOpen(false)} data={data} onDone={reload} />
      {importOpen && <ImportFastModal tenantId={t.id} tenantName={t.name} onClose={() => setImportOpen(false)} onDone={reload} />}
      <SupportModal key={String(supportOpen)} open={supportOpen} onClose={() => setSupportOpen(false)} tenantId={t.id} tenantName={t.name} />
    </>
  );
}

function BackLink() {
  return (
    <Link to="/restoranlar" className="op-btn op-btn--ghost op-btn--sm cn-back">
      <Icon name="back" /> Restoranlar
    </Link>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="op-card cn-stat">
      <span className="cn-stat__label">{label}</span>
      <span className="cn-stat__value op-num">{value}</span>
    </div>
  );
}

const STATUS_HELP: Record<TenantStatus, string> = {
  trial: 'Sinov davri: hamma narsa ishlaydi.',
  active: 'To‘lov qilingan: hamma narsa ishlaydi.',
  grace: 'Muddat o‘tgan, lekin ishlashda davom etadi (ogohlantirish bilan). Muddat yangilansa, holat o‘zi qaytadi.',
  suspended: 'Savdo va o‘zgartirish yopiladi, xodim sessiyalari yopiladi. Ega kirib tarix va hisobotlarni ko‘ra oladi.',
  deleted: 'Restoran ro‘yxatdan yashiriladi, kirish yopiladi. Ma’lumot 30 kun saqlanadi.',
};

function StatusModal({
  open,
  onClose,
  data,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  data: TenantDetail;
  onDone: () => void;
}) {
  const [status, setStatus] = useState<TenantStatus>(data.tenant.status);
  const [reason, setReason] = useState('');
  const act = useAction(async () => {
    await api.rpc('platform_set_status', { p_tenant: data.tenant.id, p_status: status, p_reason: reason || null });
    onDone();
    onClose();
  });
  const needsReason = status === 'suspended' || status === 'deleted';
  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={act.busy}
      title="Restoran holati"
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button
            variant={needsReason ? 'danger' : 'primary'}
            disabled={act.busy || status === data.tenant.status || (needsReason && reason.trim().length < 3)}
            onClick={() => void act.run()}
          >
            Saqlash
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <SelectField
        label="Yangi holat"
        value={status}
        onChange={(e) => setStatus(e.target.value as TenantStatus)}
        options={(Object.keys(STATUS) as TenantStatus[]).map((s) => ({ value: s, label: STATUS[s].label }))}
        hint={STATUS_HELP[status]}
      />
      <TextAreaField
        label={needsReason ? 'Sabab (majburiy)' : 'Sabab (ixtiyoriy)'}
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  );
}

function SupportModal({
  open,
  onClose,
  tenantId,
  tenantName,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  tenantName: string;
}) {
  const { refreshMe } = useAuth();
  const [mode, setMode] = useState<'read' | 'write'>('read');
  const [minutes, setMinutes] = useState('60');
  const [reason, setReason] = useState('');
  const [started, setStarted] = useState(false);
  const act = useAction(async () => {
    await api.rpc('platform_support_start', {
      p_tenant: tenantId,
      p_mode: mode,
      p_reason: reason,
      p_minutes: Number(minutes),
    });
    await refreshMe();
    setStarted(true);
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={act.busy}
      title={`Yordam rejimi — ${tenantName}`}
      footer={
        started ? (
          <>
            <Button onClick={onClose}>Yopish</Button>
            <a className="op-btn op-btn--primary" href={`${APP_URL}/yordam`} target="_blank" rel="noopener noreferrer">
              Restoran panelini ochish
            </a>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={act.busy}>
              Bekor qilish
            </Button>
            <Button variant="primary" disabled={act.busy || reason.trim().length < 3} onClick={() => void act.run()}>
              Boshlash
            </Button>
          </>
        )
      }
    >
      {started ? (
        <Alert tone="success" title="Yordam sessiyasi ochildi">
          Restoran paneli yangi oynada ochiladi — u yerda o‘z admin logining bilan kiring (kirish faqat o‘sha oynada saqlanadi). Ega
          panelida sessiya ko‘rinadi, har bir o‘zgarish uning jurnaliga yoziladi.
        </Alert>
      ) : (
        <>
      <Alert tone="info">
        {mode === 'read'
          ? '“Faqat ko‘rish”: baza hech qanday o‘zgarishni qabul qilmaydi.'
          : '“Ega nomidan sozlash”: menyu, sozlama, rol va xodim profillari. Pul, sklad, restoran paroli, PIN va parollarga tegilmaydi.'}{' '}
        Ega kim, qachon va nima uchun kirganini ko‘radi. Bir vaqtda bitta sessiya, muddati tugagach o‘zi yopiladi.
      </Alert>
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <SelectField
        label="Rejim"
        value={mode}
        onChange={(e) => setMode(e.target.value as 'read' | 'write')}
        options={[
          { value: 'read', label: 'Faqat ko‘rish' },
          { value: 'write', label: 'Ega nomidan sozlash' },
        ]}
      />
      <SelectField
        label="Muddat"
        value={minutes}
        onChange={(e) => setMinutes(e.target.value)}
        options={[
          { value: '30', label: '30 daqiqa' },
          { value: '60', label: '1 soat' },
          { value: '120', label: '2 soat' },
          { value: '240', label: '4 soat' },
        ]}
      />
      <TextAreaField
        label="Sabab"
        rows={2}
        placeholder="Masalan: ega menyuni sozlashda yordam so‘radi"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
        </>
      )}
    </Modal>
  );
}
